import { default as React } from 'react';
import { useMemo } from 'react';
import { StructuredMetadataTable } from '@backstage/core-components';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import type { JsonObject, JsonValue } from '@backstage/types';
import type { ReviewStepProps } from '@backstage/plugin-scaffolder-react';
import startCase from 'lodash/startCase';

/**
 * Determines if a given key is defined in the currently active schema branch.
 * This handles oneOf conditional dependencies where a property may be defined
 * only in one branch but not another.
 *
 * For example, when needsPersistentStorage is false, storageSize
 * is NOT in the active schema branch and should not appear on the review page.
 *
 * NOTE: This function only supports `oneOf` dependency patterns, which is the
 * pattern used by all SkyStage scaffolder templates. Other JSON Schema patterns
 * like `anyOf`, `allOf`, or `if/then/else` in the dependencies section are not
 * currently handled. If a template uses those patterns, extend this function.
 * As of 2026-03, all templates (microapp-hosting, create-microservices,
 * github-repo-creation) use `oneOf` exclusively for form field dependencies.
 */
function isKeyInActiveSchema(
  key: string,
  steps: ReviewStepProps['steps'],
  formData: JsonObject,
): boolean {
  for (const step of steps) {
    const schema = step.mergedSchema || step.schema;
    const properties = schema?.properties as
      | Record<string, JsonObject>
      | undefined;

    // Check if the key is directly in the top-level properties
    if (properties?.[key]) {
      return true;
    }

    // Check dependencies with oneOf
    const dependencies = schema?.dependencies as
      | Record<string, JsonObject>
      | undefined;
    if (!dependencies) continue;

    for (const [depKey, depSchema] of Object.entries(dependencies)) {
      const oneOf = depSchema.oneOf as JsonObject[] | undefined;
      if (!oneOf) continue;

      const depValue = formData[depKey];

      // Find the active oneOf branch based on the current form value
      for (const branch of oneOf) {
        const branchProps = branch.properties as
          | Record<string, JsonObject>
          | undefined;
        if (!branchProps) continue;

        const branchDepField = branchProps[depKey] as JsonObject | undefined;
        if (!branchDepField) continue;

        // Check if this branch matches the current form value
        const constValue = branchDepField.const;
        if (constValue !== undefined && constValue === depValue) {
          // This is the active branch
          if (key in branchProps) {
            return true;
          }
        }
      }
    }
  }

  return false;
}

/**
 * Formats a camelCase key into a human-readable title using startCase.
 */
function formatKey(key: string): string {
  const parts = key.split('/');
  return parts
    .filter(Boolean)
    .map(part => startCase(part))
    .join(' > ');
}

/**
 * Gets the title for a property from its schema definition.
 */
function getPropertyTitle(
  key: string,
  steps: ReviewStepProps['steps'],
  formData: JsonObject,
): string {
  for (const step of steps) {
    const schema = step.mergedSchema || step.schema;
    const properties = schema?.properties as
      | Record<string, JsonObject>
      | undefined;

    // Check top-level properties
    if (properties?.[key]?.title) {
      return properties[key].title as string;
    }

    // Check dependency branches
    const dependencies = schema?.dependencies as
      | Record<string, JsonObject>
      | undefined;
    if (!dependencies) continue;

    for (const [depKey, depSchema] of Object.entries(dependencies)) {
      const oneOf = depSchema.oneOf as JsonObject[] | undefined;
      if (!oneOf) continue;

      const depValue = formData[depKey];

      for (const branch of oneOf) {
        const branchProps = branch.properties as
          | Record<string, JsonObject>
          | undefined;
        if (!branchProps) continue;

        const branchDepField = branchProps[depKey] as JsonObject | undefined;
        if (!branchDepField) continue;

        const constValue = branchDepField.const;
        if (constValue !== undefined && constValue === depValue) {
          if (branchProps[key]?.title) {
            return branchProps[key].title as string;
          }
        }
      }
    }
  }

  return formatKey(key);
}

/**
 * Keys within object values that contain sensitive data and must never
 * be displayed on the review step. Matched case-insensitively.
 *
 * These patterns catch OAuth tokens, API keys, passwords, and other
 * credentials that custom field extensions may include in their return
 * value objects (e.g., GithubSsoLogin returns { githubOrg, email, name, token }).
 */
const SENSITIVE_KEYS = ['token', 'password', 'secret', 'apikey', 'privatekey'];

/**
 * Returns true if the given key represents a sensitive/credential value
 * that should be redacted from the review display.
 *
 * Normalizes keys by removing underscores to handle snake_case variants
 * (e.g., api_key, private_key, access_token) which are common in JSON APIs.
 */
function isSensitiveKey(key: string): boolean {
  // Normalize: lowercase and remove underscores to match snake_case variants
  // e.g., "api_key" -> "apikey", "private_key" -> "privatekey"
  const normalized = key.toLowerCase().replace(/_/g, '');
  return SENSITIVE_KEYS.some(
    sensitive => normalized === sensitive || normalized.includes(sensitive),
  );
}

/**
 * Removes sensitive fields from an object entirely so they are never
 * rendered on the review step. Returns a new object without the
 * sensitive keys.
 *
 * This function is recursive - it will redact sensitive keys from
 * nested objects as well (e.g., { config: { db: { password: "secret" } } }).
 */
function redactSensitiveFields(
  obj: Record<string, JsonValue>,
): Record<string, JsonValue> {
  const result: Record<string, JsonValue> = {};
  for (const [key, val] of Object.entries(obj)) {
    if (isSensitiveKey(key)) {
      // Completely omit sensitive fields from review display
      continue;
    }
    // Recursively process nested objects
    if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
      result[key] = redactSensitiveFields(val as Record<string, JsonValue>);
    } else {
      result[key] = val;
    }
  }
  return result;
}

/**
 * Formats a JSON value for display in the review table.
 * Sensitive fields within objects (tokens, passwords, secrets) are
 * automatically redacted to prevent credential exposure.
 */
function formatValue(value: JsonValue | undefined): React.ReactNode {
  if (value === undefined || value === null) return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value || '-';
  if (Array.isArray(value)) {
    if (value.length === 0) return '-';
    // Handle dual-env secrets: array of {key, stagingValue, productionValue} entries
    // Display keys only, never values.
    if (
      value.every(
        item =>
          typeof item === 'object' &&
          item !== null &&
          'key' in (item as object) &&
          'stagingValue' in (item as object) &&
          'productionValue' in (item as object),
      )
    ) {
      const keys = (
        value as Array<{
          key: string;
          stagingValue: string;
          productionValue: string;
        }>
      )
        .map(item => item.key)
        .filter(Boolean);
      return keys.length > 0 ? keys.join(', ') : '-';
    }
    // Handle legacy single-env secrets: array of {key, value} entries
    // Display keys only, never values.
    if (
      value.every(
        item =>
          typeof item === 'object' &&
          item !== null &&
          'key' in (item as object) &&
          'value' in (item as object),
      )
    ) {
      const keys = (value as Array<{ key: string; value: string }>)
        .map(item => item.key)
        .filter(Boolean);
      return keys.length > 0 ? keys.join(', ') : '-';
    }
    return value.map(v => String(v)).join(', ');
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, JsonValue>;
    // Redact sensitive fields FIRST (tokens, passwords, secrets) before any display
    // This ensures even { value: "ghp_token..." } patterns are protected
    const safeObj = redactSensitiveFields(obj);

    // Handle field extension objects that wrap values (e.g., { value: "url", error?: "msg" })
    // Display just the inner value instead of the full object
    if ('value' in safeObj && typeof safeObj.value === 'string') {
      return safeObj.value || '-';
    }
    return JSON.stringify(safeObj, null, 2);
  }
  return String(value);
}

/**
 * Checks if a key should be hidden based on ui:backstage review options
 * or if it's a hidden widget field (e.g., actionType).
 */
function shouldHideFromReview(
  key: string,
  steps: ReviewStepProps['steps'],
): boolean {
  // Always hide internal fields
  if (key === 'actionType') return true;

  for (const step of steps) {
    const schema = step.mergedSchema || step.schema;
    const properties = schema?.properties as
      | Record<string, JsonObject>
      | undefined;

    const propSchema = properties?.[key];
    if (propSchema) {
      // Check ui:widget: hidden
      if (propSchema['ui:widget'] === 'hidden') return true;

      // Check ui:backstage review show: false
      const backstageReview = (propSchema['ui:backstage'] as JsonObject)
        ?.review as JsonObject | undefined;
      if (backstageReview?.show === false) return true;
    }
  }

  return false;
}

/**
 * Custom ReviewStep component that filters out blank conditional fields
 * from the review page.
 *
 * This fixes the known Backstage scaffolder issue where `oneOf` dependency
 * fields persist with blank/empty values in `formData` when switching between
 * `oneOf` branches. The default ReviewState component shows ALL keys from
 * formData, even those from inactive oneOf branches.
 *
 * This component:
 * 1. Filters out keys that are not in the currently active schema branch
 * 2. Filters out empty values from stale conditional fields
 * 3. Hides internal/hidden fields (e.g., actionType)
 * 4. Redacts sensitive fields (tokens, passwords, secrets) from object values
 *    to prevent credential exposure (IDP-861)
 * 5. Preserves all other review functionality (schema titles, formatting)
 *
 * @see https://github.com/backstage/backstage/issues/24113
 * @see https://github.com/backstage/backstage/issues/12948
 */
export const CustomReviewStep = ({
  formData,
  steps,
  handleBack,
  handleCreate,
  disableButtons,
}: ReviewStepProps) => {
  // Build the filtered review data
  const reviewData = useMemo(() => {
    const result: Record<string, React.ReactNode> = {};

    for (const [key, value] of Object.entries(formData)) {
      // Skip hidden fields
      if (shouldHideFromReview(key, steps)) {
        continue;
      }

      // Check if the key is in the currently active schema branch.
      // If the key is NOT in the active schema, it's from a deselected oneOf
      // branch and should not appear on the review page - skip it regardless
      // of whether it has a value. This fixes the issue where conditional
      // fields (e.g., storageSize) show stale values on the review page when
      // their parent dependency (needsPersistentStorage) is unchecked during redeploy.
      const isInActiveSchema = isKeyInActiveSchema(key, steps, formData);
      if (!isInActiveSchema) {
        continue;
      }

      // Get the human-readable title
      const title = getPropertyTitle(key, steps, formData);

      // Format the value for display
      result[title] = formatValue(value);
    }

    return result;
  }, [formData, steps]);

  return (
    <Box>
      <StructuredMetadataTable
        metadata={reviewData}
        options={{ titleFormat: (key: string) => key }}
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          mt: 2,
        }}
      >
        <Button onClick={handleBack} disabled={disableButtons}>
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreate}
          disabled={disableButtons}
        >
          Create
        </Button>
      </Box>
    </Box>
  );
};
